import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Trash2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AIActorDraftingConditions() {
  const { actorId } = useParams();
  const navigate = useNavigate();
  
  const [actor, setActor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draftingConditions, setDraftingConditions] = useState([]);
  const [loadingConditions, setLoadingConditions] = useState(false);
  const [newCondition, setNewCondition] = useState({
    name: "",
    description: "",
    prompt: "",
    priority: 5,
    enabled: true
  });

  useEffect(() => {
    fetchActorDetails();
    fetchDraftingConditions();
  }, [actorId]);

  const fetchActorDetails = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/ai/actors/${actorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActor(response.data);
    } catch (error) {
      console.error("Failed to fetch actor details:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDraftingConditions = async () => {
    try {
      setLoadingConditions(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API}/admin/ai-email/drafting-conditions/${actorId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDraftingConditions(response.data.conditions || []);
    } catch (error) {
      console.error("Failed to fetch drafting conditions", error);
    } finally {
      setLoadingConditions(false);
    }
  };

  const createDraftingCondition = async () => {
    if (!newCondition.name || !newCondition.prompt) {
      toast.error("Name and prompt are required");
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/admin/ai-email/drafting-conditions/${actorId}`,
        newCondition,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Drafting condition created");
      setNewCondition({ name: "", description: "", prompt: "", priority: 5, enabled: true });
      fetchDraftingConditions();
    } catch (error) {
      console.error("Failed to create drafting condition", error);
      toast.error("Failed to create drafting condition");
    }
  };

  const deleteDraftingCondition = async (conditionId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `${API}/admin/ai-email/drafting-conditions/${conditionId}?actor_id=${actorId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Drafting condition deleted");
      fetchDraftingConditions();
    } catch (error) {
      console.error("Failed to delete drafting condition", error);
      toast.error("Failed to delete drafting condition");
    }
  };

  const toggleDraftingCondition = async (conditionId, enabled) => {
    try {
      const token = localStorage.getItem("token");
      await axios.patch(
        `${API}/admin/ai-email/drafting-conditions/${conditionId}`,
        { actor_id: actorId, updates: { enabled } },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Condition ${enabled ? 'enabled' : 'disabled'}`);
      fetchDraftingConditions();
    } catch (error) {
      console.error("Failed to toggle drafting condition", error);
      toast.error("Failed to update condition");
    }
  };

  const handleBack = () => {
    navigate(`/ai/actors/manage/${actorId}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <div className="container max-w-6xl mx-auto p-6 min-h-screen">
      {/* Header Section */}
      <div className="mb-8">
        <Button
          variant="ghost"
          onClick={handleBack}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Manage
        </Button>
        
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-2">Drafting Conditions</h1>
          {actor && (
            <p className="text-muted-foreground text-lg">
              {actor.name}
              {actor.experience_level && ` • ${actor.experience_level}`}
            </p>
          )}
        </div>
      </div>

      {/* Content - Exact copy from AIEmailSettings */}
      <Card>
        <CardHeader>
          <CardTitle>Personal Drafting Conditions</CardTitle>
          <CardDescription>
            Define custom rules for the AI to follow when drafting emails. These conditions are applied based on priority (1=highest).
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Add New Condition Form */}
          <div className="border rounded-lg p-4 mb-4 bg-gray-50">
            <h3 className="font-medium mb-3">Add New Condition</h3>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium block mb-1">Name *</label>
                <Input
                  placeholder="e.g., Include availability"
                  value={newCondition.name}
                  onChange={(e) => setNewCondition({ ...newCondition, name: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Description</label>
                <Input
                  placeholder="Brief description of this condition"
                  value={newCondition.description}
                  onChange={(e) => setNewCondition({ ...newCondition, description: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Prompt/Rule *</label>
                <textarea
                  className="w-full border rounded px-3 py-2 text-sm"
                  rows="3"
                  placeholder="e.g., Always mention I'm available Mon-Fri 9AM-5PM EST"
                  value={newCondition.prompt}
                  onChange={(e) => setNewCondition({ ...newCondition, prompt: e.target.value })}
                />
              </div>
              <div className="flex gap-4">
                <div className="flex-1">
                  <label className="text-sm font-medium block mb-1">Priority (1-10)</label>
                  <Input
                    type="number"
                    min="1"
                    max="10"
                    value={newCondition.priority}
                    onChange={(e) => setNewCondition({ ...newCondition, priority: parseInt(e.target.value) })}
                  />
                  <p className="text-xs text-muted-foreground mt-1">Lower number = higher priority</p>
                </div>
              </div>
              <Button onClick={createDraftingCondition} className="w-full">
                Add Condition
              </Button>
            </div>
          </div>

          {/* Existing Conditions List */}
          {loadingConditions ? (
            <p className="text-sm text-muted-foreground">Loading conditions...</p>
          ) : (draftingConditions || []).length === 0 ? (
            <p className="text-sm text-muted-foreground">No drafting conditions created yet. Add your first condition above!</p>
          ) : (
            <div className="space-y-3">
              {draftingConditions.map((condition) => (
                <div key={condition.id} className="border rounded-lg p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-medium">{condition.name}</h4>
                        <Badge variant="outline" className="text-xs">
                          Priority: {condition.priority}
                        </Badge>
                        {!condition.enabled && (
                          <Badge variant="secondary" className="text-xs">Disabled</Badge>
                        )}
                      </div>
                      {condition.description && (
                        <p className="text-sm text-muted-foreground mb-2">{condition.description}</p>
                      )}
                      <p className="text-sm bg-gray-50 p-2 rounded">{condition.prompt}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant={condition.enabled ? "default" : "outline"}
                        onClick={() => toggleDraftingCondition(condition.id, !condition.enabled)}
                      >
                        {condition.enabled ? "Enabled" : "Disabled"}
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => {
                          if (confirm("Delete this condition?")) {
                            deleteDraftingCondition(condition.id);
                          }
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
